import { api } from '@/api';
import {
  EnumerationEvaluationOptionDto,
  EvaluationParameterDto,
  ManualEnumerationInputDto,
  ManualEnumerationInputDtoTypeEnum,
  ManualNumericInputDto,
  ManualNumericInputDtoTypeEnum,
  ManualTaskWorkInputDto,
  ManualTaskWorkItemDto,
  ManualTaskWorkResultDto,
  ManualTaskWorkStepDto,
  UpdateManualEnumerationInputDtoTypeEnum,
  UpdateManualNumericInputDtoTypeEnum,
} from '@/api/generator';
import { groupBy, isNumber } from 'lodash';
import { computed, MaybeRefOrGetter, ref, toRef } from 'vue';
import { useJobOccurrence } from './useJobOccurrence';
import { useSupportSystemGroups } from '@/views/jobConfigurations/utils/useSupportSystemGroups';

export function useManualTask(workitem: MaybeRefOrGetter<ManualTaskWorkItemDto>) {
  const workitemRef = toRef(workitem);

  const { supportSystems: allSupportSystems } = useJobOccurrence();

  const { supportSystems, supportSystemGroups, numGroups } = useSupportSystemGroups(
    computed(() => workitemRef.value.supportSystemIds ?? undefined),
    computed(() => allSupportSystems.value ?? undefined),
  );

  return {
    supportSystems,
    supportSystemGroups,
    numGroups,
    useManualTaskStep,
  };
}

export function useManualTaskStep(step: MaybeRefOrGetter<ManualTaskWorkStepDto>) {
  const updating = ref<boolean>(false);

  const { jobOccurrence, reload, discardResult, toggleIgnoreResult } = useJobOccurrence();

  const stepRef = toRef(step);

  const results = computed(() =>
    groupBy(stepRef.value.results, (result) => result?.evaluationResult?.evaluationParameterId),
  );

  async function addInput(
    evaluation: EvaluationParameterDto,
    inputValue: EnumerationEvaluationOptionDto | number,
  ) {
    updating.value = true;

    const manualTaskWorkInputDto: ManualTaskWorkInputDto = isNumber(inputValue)
      ? ({
          inputValue,
          $type:
            ManualNumericInputDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobExecutionDtoManualNumericInputDtoPtwPtw100AppZeroApplicationShared,
        } satisfies ManualNumericInputDto)
      : ({
          enumerationOptionId: inputValue.id,
          $type:
            ManualEnumerationInputDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobExecutionDtoManualEnumerationInputDtoPtwPtw100AppZeroApplicationShared,
        } satisfies ManualEnumerationInputDto);

    if (jobOccurrence.value.id) {
      const { success } = await api.jobOccurrencesApi.startManualWorkStep({
        id: jobOccurrence.value.id,
        workStepId: stepRef.value.id,
        evaluationId: evaluation.id,
        manualTaskWorkInputDto,
      });

      if (success) {
        reload();
      }

      updating.value = false;
      return success;
    }

    updating.value = false;
  }

  async function updateResult(
    result: ManualTaskWorkResultDto,
    input: number | EnumerationEvaluationOptionDto,
  ) {
    updating.value = true;

    const { success } = await api.resultsApi.updateResult({
      id: result.id,
      updateResultDto: isNumber(input)
        ? {
            inputValue: input,
            $type:
              UpdateManualNumericInputDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobExecutionDtoUpdateManualNumericInputDtoPtwPtw100AppZeroApplicationShared,
          }
        : {
            enumerationOptionId: input.id,
            $type:
              UpdateManualEnumerationInputDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobExecutionDtoUpdateManualEnumerationInputDtoPtwPtw100AppZeroApplicationShared,
          },
    });

    if (success) {
      reload();
    }

    updating.value = false;
  }

  return {
    results,
    addInput,
    discardResult,
    updateResult,
    updating,
    toggleIgnoreResult,
    reload,
  };
}
